import { getProjectEnvironmentsPageSize, getProjectListPageSize } from '@gonfalon/dogfood-flags/dogfood-flags';
// oxlint-disable-next-line no-restricted-imports
import { fromJS, Record, Set } from '@gonfalon/immutable-shim/immutableShim';
import { isEmpty } from 'lodash';
import qs from 'qs';

import type {
  EnvironmentFiltersType,
  EnvironmentsFiltersQuery,
  ProjectFiltersQuery,
  ProjectFiltersType,
} from '#utils/projects/projectTypes';

export class ProjectFilters extends Record<ProjectFiltersType>({
  tagFilter: Set(),
  textFilter: '',
  sort: '-createdOn',
  limit: 0,
  offset: 0,
}) {
  toQuery() {
    const ret: ProjectFiltersQuery = {};
    if (!this.tagFilter.isEmpty()) {
      ret.tagFilter = this.tagFilter.toList().sort().toJS();
    }
    if (!isEmpty(this.textFilter)) {
      ret.textFilter = this.textFilter;
    }
    if (!isEmpty(this.sort) && this.sort !== '-createdOn') {
      ret.sort = this.sort;
    }
    return ret;
  }
  withQuery(query: string) {
    return this.set('offset', 0).set('textFilter', query);
  }
  toBackendQueryString() {
    const q: Partial<ProjectFiltersQuery> & { filter?: string } = {};
    const filterParts = [];

    if (this.textFilter && this.textFilter !== '') {
      filterParts.push(`query:${this.textFilter}`);
    }

    if (this.tagFilter?.size) {
      filterParts.push(`tags:${this.tagFilter.toArray().join('+')}`);
    }

    q.filter = filterParts.join(',');

    if (this.limit && this.limit > 0) {
      q.limit = this.limit;
    }
    if (this.offset) {
      q.offset = this.offset;
    }
    if (this.sort) {
      q.sort = this.sort;
    }

    return qs.stringify(q, { indices: false, addQueryPrefix: true });
  }
  toFrontendQueryString(options?: { omitPaginationParams?: boolean }) {
    const q = {} as EnvironmentsFiltersQuery;

    if (!isEmpty(this.textFilter)) {
      q.textFilter = this.textFilter;
    }
    if (options?.omitPaginationParams) {
      return q;
    }

    const pageSize = getProjectEnvironmentsPageSize();

    if (this.limit && this.limit > 0 && (this.offset || this.limit !== pageSize)) {
      q.limit = this.limit;
    }
    if (this.offset) {
      q.offset = this.offset;
    }
    if (this.tagFilter) {
      q.tagFilter = this.tagFilter.toArray();
    }
    if (this.sort) {
      q.sort = this.sort;
    }
    return q;
  }
  toQueryString(options?: { omitPaginationParams?: boolean }) {
    return qs.stringify(this.toFrontendQueryString(options), { indices: false });
  }
}

export class EnvironmentsFilters extends Record<EnvironmentFiltersType>({
  tagFilter: Set(),
  textFilter: '',
  limit: -1,
  offset: 0,
  sort: '',
}) {
  toBackendQueryString() {
    const q: Partial<EnvironmentFiltersType> & { filter?: string } = {};
    const filterParts = [];

    if (this.textFilter && this.textFilter !== '') {
      filterParts.push(`query:${this.textFilter}`);
    }

    if (this.tagFilter?.size) {
      filterParts.push(`tags:${this.tagFilter.toArray().join('+')}`);
    }

    q.filter = filterParts.join(',');
    if (this.limit && this.limit > 0) {
      q.limit = this.limit;
    }
    if (this.offset) {
      q.offset = this.offset;
    }

    q.sort = 'critical';

    return qs.stringify(q, { indices: false, addQueryPrefix: true });
  }
  toFrontendEnvironmentsQueryString(options?: { omitPaginationParams?: boolean }) {
    const q = {} as EnvironmentsFiltersQuery;

    if (!isEmpty(this.textFilter)) {
      q.textFilter = this.textFilter;
    }
    if (options?.omitPaginationParams) {
      return q;
    }

    const pageSize = getProjectEnvironmentsPageSize();

    if (this.limit && this.limit > 0 && (this.offset || this.limit !== pageSize)) {
      q.limit = this.limit;
    }
    if (this.offset) {
      q.offset = this.offset;
    }
    if (this.tagFilter) {
      q.tagFilter = this.tagFilter.toArray();
    }
    return q;
  }
  toQueryString(options?: { omitPaginationParams?: boolean }) {
    return qs.stringify(this.toFrontendEnvironmentsQueryString(options), { indices: false });
  }
}

export function createProjectFilters(props = {}) {
  return new ProjectFilters(fromJS(props));
}

export function createEnvironmentsFilters(props = {}) {
  return new EnvironmentsFilters(fromJS(props));
}

export function createEnvironmentsFiltersFromQuery(queryMap: EnvironmentsFiltersQuery) {
  const textFilter = queryMap ? queryMap?.textFilter : '';
  const tagFilter = queryMap ? queryMap.tagFilter : Set();
  const sort = queryMap ? queryMap.sort : 'createdOn';
  let tagsArray;
  if (Set.isSet(tagFilter)) {
    tagsArray = tagFilter;
  } else if (Array.isArray(tagFilter)) {
    tagsArray = Set([...tagFilter]);
  } else {
    tagsArray = tagFilter ? Set([tagFilter]) : Set();
  }
  const props = {
    textFilter: textFilter || '',
    tagFilter: tagsArray,
    sort: sort || 'createdOn',
    offset: queryMap?.offset || 0,
    limit: queryMap?.limit || getProjectEnvironmentsPageSize(),
  };
  return createEnvironmentsFilters(props);
}

export function createProjectFiltersFromQuery(queryMap: ProjectFiltersQuery) {
  const textFilter = queryMap ? queryMap.textFilter : '';
  const tagFilter = queryMap ? queryMap.tagFilter : Set();
  const sort = queryMap ? queryMap.sort : 'createdOn';
  let tagsArray;
  if (Set.isSet(tagFilter)) {
    tagsArray = tagFilter;
  } else if (Array.isArray(tagFilter)) {
    tagsArray = Set([...tagFilter]);
  } else {
    tagsArray = tagFilter ? Set([tagFilter]) : Set();
  }
  const props = {
    textFilter: textFilter || '',
    tagFilter: tagsArray,
    sort: sort || '-createdOn',
    limit: queryMap?.limit || getProjectListPageSize(),
    offset: queryMap?.offset || 0,
  };

  return createProjectFilters(props);
}
